/**
 * Platform identity.
 *
 * The four channels 4ocean actually publishes on, each with the colour people
 * already associate with it. Recognition is the whole job here: on a calendar
 * cell there is room for a dot, not a word, and a dot only works if its colour
 * means something before you read anything.
 *
 * Unknown platforms are not dropped — Angel types this field freehand, and a
 * silently vanishing platform is worse than an uncoloured one.
 */

export interface PlatformStyle {
  key: string;
  label: string;
  /** Two letters for the pill; the dot uses colour alone. */
  initials: string;
  color: string;
}

const KNOWN: PlatformStyle[] = [
  { key: 'instagram', label: 'Instagram', initials: 'IG', color: '#d6249f' },
  { key: 'tiktok', label: 'TikTok', initials: 'TT', color: '#00c4cc' },
  { key: 'youtube', label: 'YouTube', initials: 'YT', color: '#ff0033' },
  { key: 'facebook', label: 'Facebook', initials: 'FB', color: '#1877f2' },
  { key: 'linkedin', label: 'LinkedIn', initials: 'LI', color: '#0a66c2' },
];

/** Matches on a normalised name, so "Tik Tok" and "tiktok" are the same thing. */
export function platformStyle(raw: string): PlatformStyle {
  const normalized = raw.toLowerCase().replace(/[^a-z]/g, '');
  const match = KNOWN.find((entry) => entry.key === normalized);
  if (match !== undefined) {
    return match;
  }
  const trimmed = raw.trim();
  return {
    key: normalized,
    label: trimmed,
    initials: trimmed.slice(0, 2).toUpperCase(),
    // Neutral rather than a generated colour: an invented hue would imply a
    // meaning the platform does not have.
    color: 'var(--text-muted)',
  };
}

export function platformStyles(platforms: string[]): PlatformStyle[] {
  return platforms.map(platformStyle).filter((entry) => entry.label !== '');
}

/** "Angel Buenaobra" from a display name, or from an email when there is none. */
export function personLabel(person: { display_name: string; email: string }): string {
  const name = person.display_name.trim();
  if (name !== '') {
    return name;
  }
  // angel.buenaobra@4ocean.com → Angel Buenaobra. An email in a table cell is
  // a system detail leaking into a place people read names.
  const local = person.email.split('@')[0] ?? person.email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
